import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { IconSlot, MenuRow, MenuSurface } from '~/design-system/components/v1_5/Header';
// The LEAF, not `TextPropertiesPanel` — importing the panel would pull `FontPicker`, `CreditSlider`
// and the DeckGen editor contexts into the `v1_5/Preview` barrel, and from there into the deck
// workspace and every Preview story. Same three constants, same values.
import {
  FONT_SIZE_SCALE,
  WEIGHT_OPTIONS,
  nearestFontSizeIndex,
} from '~/design-system/components/Toolbar/PropertiesPanel/textScale';
import { cn } from '~/lib/utils';
import { TEXT_COLOR_PANEL_WIDTH, TextColorPanel } from './TextColorPanel';
import { TEXT_FONT_PANEL_WIDTH, TextFontPanel } from './TextFontPanel';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './TextToolbar.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/** How the text sits in its box. Four values, and `justify` is the one the Toolbar's own
 *  `ALIGN_ITEMS` never offered — which is why that constant is not reused here. */
export type TextAlign = 'left' | 'center' | 'right' | 'justify';

/**
 * Which of the bar's four menus is open.
 *
 * COLOUR IS ONE OF THEM NOW. It was left out on the argument that a colour popover is a whole
 * instrument and belongs to whoever knows the document's palette — so the chip wore the same chevron
 * as the other three and, on press, did nothing at all. Nothing is worse than a control that looks
 * like the ones beside it and is inert.
 *
 * What it opens is `TextColorPanel`, which is deliberately small: a square, a hue strip and a hex
 * field, with no gradients, no alpha and no swatch row. The swatches are the part that genuinely
 * needs the document's palette, and they are the part that is not here.
 *
 * Living in this union rather than in its own piece of state is the point of the change: the panel
 * inherits the outside-press dismissal, the Escape handler, the exit transition and the focus guard
 * that the other three menus already have, and a fourth mechanism cannot drift from the first three.
 */
type TextMenu = 'family' | 'color' | 'size' | 'weight' | 'align';

/**
 * Must match `--hv15-dur-menu-out`. The menu stays mounted this long after it closes so the exit
 * transition has something to run on.
 *
 * The FIFTH copy of this shape in v1.5 — `HeaderV15`, `DirectoryPanel`, `ShareMenu` and
 * `PreviewBar` each carry their own, and `PreviewBar`'s comment has been asking for the hook that
 * would collapse them since it was the fourth. This one is a little worse than the others: it holds
 * a menu NAME rather than a boolean, because three menus share one layer here.
 */
const MENU_EXIT_MS = 120;

/**
 * Menu widths. **CHOSEN IN CODE — NOT MEASURED.** The board draws the bar at rest; no artboard
 * opens any of these three.
 *
 * They are wider than the chips they hang off on purpose. A menu sized to its trigger reads as the
 * chip stretching downward, and these are not that — they are the surface every other v1.5 popover
 * uses, and the family list in particular has to hold names the 138px chip is already ellipsising.
 */
/** The font panel's own, from the component that draws it: wide enough to judge a display face on. */
const FAMILY_MENU_WIDTH = TEXT_FONT_PANEL_WIDTH;
/** The colour panel's, from the component that draws it: sized by its square, not by a row of text. */
const COLOR_MENU_WIDTH = TEXT_COLOR_PANEL_WIDTH;
const WEIGHT_MENU_WIDTH = 168;
const ALIGN_MENU_WIDTH = 168;
/** The narrowest of the five: its rows are two or three digits and a tick. */
const SIZE_MENU_WIDTH = 108;

/**
 * The four alignments, in reading order.
 *
 * The glyphs are the `text-align-*` set and the prefix matters: the registry also carries a bare
 * `align-*` family, and it is not a synonym. `align-center` and `align-justify` happen to be
 * byte-identical to their `text-align-*` twins, while `align-left` is a completely different
 * drawing — the flex-distribute glyph, bars against a vertical rule. Half a family that agrees is
 * worse than one that disagrees everywhere, so the whole run is spelled `text-align-*`.
 */
export const ALIGN_ORDER: TextAlign[] = ['left', 'center', 'right', 'justify'];

const ALIGN_ICON: Record<TextAlign, string> = {
  left: 'text-align-left',
  center: 'text-align-center',
  right: 'text-align-right',
  justify: 'text-align-justify',
};

/** The words the menu prints, and the ones the properties panel's Text section reuses so the two agree. */
export const ALIGN_LABEL: Record<TextAlign, string> = {
  left: 'Left',
  center: 'Center',
  right: 'Right',
  justify: 'Justify',
};

/**
 * One stop along `FONT_SIZE_SCALE`, clamped at both ends.
 *
 * An off-scale size is snapped to its nearest stop BEFORE the step, which is what
 * `nearestFontSizeIndex` exists for — and it has a consequence worth knowing: from 30, stepping
 * down lands on 24 rather than on 32, because 30 is already 32 as far as the ladder is concerned.
 * That is the right answer for a stepper. The alternative — "the nearest stop strictly below" —
 * makes the two buttons asymmetric, so pressing down then up would not return you to where you
 * started.
 *
 * It CLAMPS rather than returning undefined, so the buttons never have to disable. `ZoomCluster`'s
 * reasoning applies unchanged: a disabled button cannot hold focus, so a keyboard user who steps
 * down to 8px has the control blurred out from under them and the next Tab restarts at the top of
 * the document. The press at the end of the ladder is a no-op instead of a trap.
 */
export function stepFontSize(size: number, direction: 1 | -1): number {
  const index = nearestFontSizeIndex(size);
  const next = Math.min(FONT_SIZE_SCALE.length - 1, Math.max(0, index + direction));
  return FONT_SIZE_SCALE[next];
}

/**
 * THE WEIGHT MENU IS THE FONT'S, not the product's.
 *
 * `WEIGHT_OPTIONS` is the six-step ladder every text control in this app shares, and on an artifact
 * it was mostly fiction: a document that loads Inter at 300 and 400 has no Semibold face, so picking
 * Semibold left the engine to fall back to the nearest one it had and the menu had told the user
 * something untrue. The host now asks the document what it can actually set.
 *
 * TWO RULES, and the second is what keeps the chip honest. Nothing is hidden when the answer is
 * unknown — see the `weights` prop. And the CURRENT weight is always in the list, even when the font
 * has no face for it: artifact text is authored freely, so a heading really can be sitting at a weight
 * its own family cannot render, and a menu that omitted it would be a chip whose value is not one of
 * its own options.
 *
 * Pure and exported because the bar is no longer the only place a weight is picked: the properties
 * panel's Text section offers the same list, and two copies of these rules would be two menus that
 * disagree about which faces a font has.
 */
export function weightOptionsFor(
  weights: string[] | undefined,
  weight: string,
): typeof WEIGHT_OPTIONS {
  if (!weights || weights.length === 0) return WEIGHT_OPTIONS;
  const allowed = new Set([...weights, weight]);
  const shown = WEIGHT_OPTIONS.filter((option) => allowed.has(option.value));
  // A family whose every face is outside the ladder — 250, say — would otherwise leave an empty
  // menu. The ladder is the fallback there: an empty popover is not a smaller lie, it is a dead end.
  return shown.length > 0 ? shown : WEIGHT_OPTIONS;
}

export interface TextToolbarProps {
  fontFamily: string;
  /**
   * The families the DOCUMENT already has.
   *
   * No longer the whole menu — the menu is the font catalogue, ~1,900 families with search. These are
   * the section at the top of it, the fonts already loaded where the text lives, which are the ones a
   * pick costs nothing for. Empty is an ordinary state now rather than a disabled control: a document
   * that has told us nothing still gets a picker.
   */
  fontFamilies?: string[];
  /**
   * A family, and the stylesheet URL the artifact needs in order to HAVE that face — null when it
   * already has it, or when the family is not one the catalogue can serve.
   *
   * The second argument is the whole reason this picker can offer more than the document's own fonts.
   * An artifact is a sandboxed document on an opaque origin, so a font loaded on this side is not a
   * font it can set; the host posts the stylesheet into the frame and writes it into the source.
   */
  onFontFamily: (family: string, href: string | null) => void;
  /**
   * Any CSS colour, as the document reports it — `getComputedStyle` answers `rgb(17, 17, 17)`
   * however the author wrote it, and the panel parses that.
   */
  color: string;
  /**
   * A `#rrggbb` string, on every step of a drag rather than on release.
   *
   * Live because the whole point of a colour picker over an artifact is watching the headline change
   * while you move. The host is expected to be debouncing what it PERSISTS, which is a different
   * question from what it paints.
   */
  onColor: (hex: string) => void;
  /** A `WEIGHT_OPTIONS` value: '300'…'800'. */
  weight: string;
  onWeight: (weight: string) => void;
  /**
   * Which weights this family actually HAS, as CSS values.
   *
   * Omit when it is not known — a system font whose faces cannot be enumerated, or a host that has not
   * asked. Omitted means the full ladder, which is the honest reading: not knowing a weight is missing
   * is not the same as knowing it is there, and hiding rows on a guess would trade one lie for another.
   */
  weights?: string[];
  /** px. Steps land on `FONT_SIZE_SCALE`. */
  size: number;
  onSize: (px: number) => void;
  align: TextAlign;
  onAlign: (align: TextAlign) => void;
  bold: boolean;
  onBold: () => void;
  italic: boolean;
  onItalic: () => void;
  /**
   * The properties the thing being styled DOES NOT AGREE WITH ITSELF ABOUT.
   *
   * Only ever populated when a run of words is selected rather than a whole element — a sentence half
   * at 128 and half at 40 has no size, and a box reading 128 over it would make the user's next press
   * a change they never asked for. A control named here draws an indeterminate state and does not
   * pretend to a value; pressing it still applies, which is the point of it.
   */
  mixed?: {
    fontFamily?: boolean;
    fontWeight?: boolean;
    fontSize?: boolean;
    color?: boolean;
  };
  theme?: HeaderTheme;
  /** Where the bar goes. It follows the text element being edited, which only the host knows. */
  className?: string;
}

/**
 * The bar that appears while you are typing into a text element inside an artifact: family, colour,
 * weight, size, then bold / italic / align.
 *
 * ## Fully controlled, with one exception
 *
 * Every VALUE is the host's — the bar holds none of them, and pressing bold does not paint the
 * button until the host says the text is bold. The exception is which of the three menus is open,
 * which the bar owns because it is not a fact about the text: it is a fact about this bar, it dies
 * with it, and a host asked to thread `openMenu` through would be storing UI state on behalf of a
 * component it cannot see.
 *
 * ## Why it does not place itself
 *
 * `ZoomCluster` owns a corner of the stage because there is exactly one place a zoom readout goes.
 * This bar tracks a text element inside a scaled, scrolled artifact, so its position is the product
 * of the fit, the scroll offset and the element's own box — three things the host has and this
 * component does not. It renders a 44px bar and the host puts it somewhere.
 */
export function TextToolbar({
  fontFamily,
  fontFamilies,
  onFontFamily,
  mixed,
  color,
  onColor,
  weight,
  onWeight,
  weights,
  size,
  onSize,
  align,
  onAlign,
  bold,
  onBold,
  italic,
  onItalic,
  theme: themeProp,
  className,
}: TextToolbarProps) {
  const theme = useV15Theme(themeProp);
  const [open, setOpen] = useState<TextMenu | null>(null);
  const [rendered, setRendered] = useState<TextMenu | null>(null);
  const closing = rendered !== null && open === null;
  /** Each chip and the menu it opens: the region a press may land in without dismissing it. */
  const anchors = useRef<Partial<Record<TextMenu, HTMLSpanElement | null>>>({});

  useEffect(() => {
    if (open) {
      setRendered(open);
      return;
    }
    if (!rendered) return;
    const timer = setTimeout(() => setRendered(null), MENU_EXIT_MS);
    return () => clearTimeout(timer);
  }, [open, rendered]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    // The OPEN menu's own anchor is skipped so pressing its chip again is left to that chip's click
    // handler — a pointerdown that closed first would make the click that follows reopen what it
    // just shut. Every other anchor is outside, so pressing a second chip closes the first menu and
    // opens the second in one press rather than swallowing it as a dismissal.
    const onDown = (e: PointerEvent) => {
      if (anchors.current[open]?.contains(e.target as Node)) return;
      setOpen(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  const families = fontFamilies ?? [];
  const weightLabel = WEIGHT_OPTIONS.find((option) => option.value === weight)?.label ?? weight;

  /** The font's weights, not the product's — see `weightOptionsFor`. */
  const weightOptions = useMemo(() => weightOptionsFor(weights, weight), [weights, weight]);

  /** Toggle, so the chip that opened a menu is also the way to shut it. */
  const toggle = (menu: TextMenu) => setOpen((current) => (current === menu ? null : menu));

  return (
    <div
      // The accurate role: a run of controls acting on one thing. It keeps every control in the tab
      // order rather than implementing APG's roving tabindex — the bar is transient, it appears
      // because you are already typing, and a keyboard user reaching it wants to Tab to the one
      // control they came for, not to arrow through seven of them.
      role="toolbar"
      aria-label="Text"
      className={cn(themes[theme], styles.bar, className)}
      /**
       * THE BAR MUST NOT TAKE FOCUS. Nothing else on it works if it does.
       *
       * The text this bar edits is a caret inside a sandboxed iframe, and focus is a document-wide
       * thing: pressing anything out here moves it, the iframe blurs, and a blur is how the editor in
       * there decides an edit is finished — it commits and closes. So reaching for the font menu ended
       * the edit before the menu had opened, the ring vanished, and the change went nowhere. Measured,
       * not theorised: it is what "the selection disappears as soon as I try to select something" was.
       *
       * `preventDefault` on mousedown is the whole fix, and it is the same one every floating text
       * toolbar uses. It suppresses the focus change only — `click` still fires, so every control here
       * behaves exactly as before, and the caret stays where the user left it.
       *
       * On the ROOT rather than per control, so it covers the chips, the icon buttons, the stepper and
       * the menus that open inside it — a control added later cannot forget.
       */
      onMouseDown={(event) => event.preventDefault()}
    >
      <span
        className={styles.anchor}
        ref={(el) => {
          anchors.current.family = el;
        }}
      >
        <button
          type="button"
          className={cn(styles.chip, styles.pressable, styles.family)}
          // The visible string is the family's name, which says what the value IS and not what the
          // control is for. Screen readers get both.
          aria-label={`Font: ${fontFamily}`}
          aria-haspopup="menu"
          aria-expanded={open === 'family'}
          // NO LONGER DISABLES, and the reason the rule changed is the panel behind it. This chip
          // used to open a list of the host's families and had nothing to show when there were none.
          // What it opens now is the font catalogue, which is the same ~1,900 families whatever the
          // document happens to have loaded — so there is no state in which pressing it is pointless.
          onClick={() => toggle('family')}
        >
          {/* The name in ITS OWN FACE, which is what a font control says with instead of a swatch.
              Runtime value, so it is one of the two inline styles on this bar. */}
          {/* MIXED IS NOT A FAMILY, so it is not set in one. A word rendered in the face of whichever
              run happened to be first would be the control asserting the very thing it is reporting it
              cannot. */}
          <span
            className={cn(styles.label, mixed?.fontFamily && styles.indeterminate)}
            style={mixed?.fontFamily ? undefined : { fontFamily: `'${fontFamily}'` }}
          >
            {mixed?.fontFamily ? 'Mixed' : fontFamily}
          </span>
          <span className={styles.spring} aria-hidden />
          <DeckIcon name="arrow-down-s" size={14} className={styles.caret} />
        </button>
        {rendered === 'family' ? (
          <div className={styles.menuLayer} data-closing={closing}>
            <MenuSurface width={FAMILY_MENU_WIDTH} theme={theme} label="Font" role="group">
              <TextFontPanel
                family={fontFamily}
                documentFamilies={families}
                theme={theme}
                onPick={(family, href) => {
                  setOpen(null);
                  // Picking the live one is a dismissal, not a change — the same cut `ShareMenu`
                  // makes. A host that re-renders the artifact on every `onFontFamily` should not
                  // be asked to do it for a press that changed nothing.
                  if (family !== fontFamily) onFontFamily(family, href);
                }}
              />
            </MenuSurface>
          </div>
        ) : null}
      </span>

      <span
        className={styles.anchor}
        ref={(el) => {
          anchors.current.color = el;
        }}
      >
        <button
          type="button"
          className={cn(styles.chip, styles.pressable, styles.color)}
          // The only genuine runtime value on this bar, and therefore the only inline style: it is an
          // arbitrary CSS colour from the document, so no stylesheet can know it in advance.
          style={{ '--hv15-text-swatch': color } as React.CSSProperties}
          aria-label={`Text colour: ${color}`}
          // `dialog` rather than `menu`, and it is the one chip here where that is true: what opens
          // is a square and a strip you drag, not a list of rows you choose from. `aria-expanded`
          // can be stated now that this bar owns the panel — it used to be omitted because the
          // popover was the host's and this control genuinely could not observe it.
          aria-haspopup="dialog"
          aria-expanded={open === 'color'}
          onClick={() => toggle('color')}
        >
          {/* The swatch cannot show two colours, so it shows that there are two — see `.swatchMixed`. */}
          <span className={cn(styles.swatch, mixed?.color && styles.swatchMixed)} aria-hidden />
          <DeckIcon name="arrow-down-s" size={14} className={styles.caret} />
        </button>
        {rendered === 'color' ? (
          <div className={styles.menuLayer} data-closing={closing}>
            <MenuSurface width={COLOR_MENU_WIDTH} theme={theme} label="Text colour" role="group">
              <TextColorPanel color={color} onColor={onColor} theme={theme} />
            </MenuSurface>
          </div>
        ) : null}
      </span>

      <span
        className={styles.anchor}
        ref={(el) => {
          anchors.current.weight = el;
        }}
      >
        <button
          type="button"
          className={cn(styles.chip, styles.pressable, styles.weight)}
          aria-label={`Font weight: ${weightLabel}`}
          aria-haspopup="menu"
          aria-expanded={open === 'weight'}
          onClick={() => toggle('weight')}
        >
          {/* The word, not the number. `500` is the value the host stores and `Medium` is the thing
              the designer picked; a chip 88px wide has room for exactly one of them. */}
          <span className={cn(styles.label, mixed?.fontWeight && styles.indeterminate)}>
            {mixed?.fontWeight ? 'Mixed' : weightLabel}
          </span>
          <span className={styles.spring} aria-hidden />
          <DeckIcon name="arrow-down-s" size={14} className={styles.caret} />
        </button>
        {rendered === 'weight' ? (
          <div className={styles.menuLayer} data-closing={closing}>
            <MenuSurface width={WEIGHT_MENU_WIDTH} theme={theme} label="Font weight">
              {weightOptions.map((option) => (
                <MenuRow
                  key={option.value}
                  label={option.label}
                  theme={theme}
                  trailing={
                    option.value === weight ? (
                      <span className={styles.tick}>
                        <DeckIcon name="check" size={16} />
                      </span>
                    ) : undefined
                  }
                  onClick={() => {
                    setOpen(null);
                    if (option.value !== weight) onWeight(option.value);
                  }}
                />
              ))}
            </MenuSurface>
          </div>
        ) : null}
      </span>

      {/* A group and not a button: the chip itself does nothing, its two steppers do. The label
          carries the current size so entering the group announces the value the two buttons are
          about to change — otherwise "Increase text size" is an instruction with no subject. */}
      <span
        className={styles.anchor}
        ref={(el) => {
          anchors.current.size = el;
        }}
      >
        {/**
         * THE SIZE CHIP DOES TWO THINGS, and it is the only control on the bar that does.
         *
         * The stepper is what the board drew, and it is right for the thing it is good at: nudging a
         * headline one stop at a time while watching it. It is wrong for everything else — going from
         * 16 to 188 is nine presses, and the ladder is not printed anywhere, so a user stepping
         * through it is discovering the scale one number at a time (Beka, 2026-09-05, "we need to have
         * dropdown menu for the font size").
         *
         * So the VALUE opens the ladder and the arrows still walk it. That is the same division a
         * numeric field has had in every design tool for twenty years, and it is why this stayed one
         * chip rather than becoming a chip and a menu: they are two ways to set one number, and
         * splitting them would put the same value in two places on a 472px bar.
         */}
        <div className={cn(styles.chip, styles.size)} role="group" aria-label={`Text size: ${size}px`}>
          <button
            type="button"
            className={styles.sizeValue}
            aria-label={`Text size: ${size}px`}
            aria-haspopup="menu"
            aria-expanded={open === 'size'}
            onClick={() => toggle('size')}
          >
            {/* An en dash rather than a number, and rather than a blank: blank reads as a control
                that failed to load, and this one knows exactly what it is looking at. */}
            <span className={cn(styles.label, mixed?.fontSize && styles.indeterminate)}>
              {mixed?.fontSize ? '\u2013' : size}
            </span>
            {/* Inside the button, not beside it: the dead space between the number and the stepper is
                the easiest part of this chip to hit, and leaving it inert would make a 62px control
                feel like a 20px one. */}
            <span className={styles.spring} aria-hidden />
          </button>
          <span className={styles.stepper}>
            <button
              type="button"
              className={styles.step}
              aria-label="Increase text size"
              onClick={() => onSize(stepFontSize(size, 1))}
            >
              <DeckIcon name="arrow-up-s" size={11} />
            </button>
            <button
              type="button"
              className={styles.step}
              aria-label="Decrease text size"
              onClick={() => onSize(stepFontSize(size, -1))}
            >
              <DeckIcon name="arrow-down-s" size={11} />
            </button>
          </span>
        </div>
        {rendered === 'size' ? (
          <div className={styles.menuLayer} data-closing={closing}>
            <MenuSurface width={SIZE_MENU_WIDTH} theme={theme} label="Text size" className={styles.sizeSurface}>
              {FONT_SIZE_SCALE.map((stop) => {
                // The live size may be OFF the ladder — artifact text is authored freely — so the tick
                // goes on the stop the stepper would treat it as. Ticking nothing would be accurate and
                // useless: the menu would open with no answer to "where am I".
                const current = stop === FONT_SIZE_SCALE[nearestFontSizeIndex(size)];
                return (
                  <MenuRow
                    key={stop}
                    label={String(stop)}
                    theme={theme}
                    /**
                     * OPEN ON THE CURRENT SIZE, rather than at the top of a fifteen-stop list.
                     *
                     * Written against the surface's own `scrollTop` and NOT with `scrollIntoView`,
                     * which is the obvious call and the wrong one: it scrolls every scrollable
                     * ancestor, so a menu opened near the bottom of a scrolled page would jump the
                     * page as well as the list. `.surface` is `position: relative`, so it is this
                     * row's offset parent and the arithmetic is exact.
                     *
                     * No smooth behaviour: the menu is 120ms old, and a scroll easing inside an
                     * entrance transition reads as the list settling rather than as it arriving.
                     */
                    ref={
                      current
                        ? (el: HTMLButtonElement | null) => {
                            const surface = el?.offsetParent;
                            if (!el || !(surface instanceof HTMLElement)) return;
                            surface.scrollTop =
                              el.offsetTop - surface.clientHeight / 2 + el.offsetHeight / 2;
                          }
                        : undefined
                    }
                    trailing={
                      current ? (
                        <span className={styles.tick}>
                          <DeckIcon name="check" size={16} />
                        </span>
                      ) : undefined
                    }
                    onClick={() => {
                      setOpen(null);
                      if (stop !== size) onSize(stop);
                    }}
                  />
                );
              })}
            </MenuSurface>
          </div>
        ) : null}
      </span>

      <span className={styles.spacerWide} aria-hidden />

      {/* `pressed` rather than `activeIcon`, which is the case `IconSlot`'s own prop documents:
          there is no `bold-fill` in the registry, so the lit state paints a chip and keeps the
          glyph — and without this it would announce identically to the control beside it. */}
      <IconSlot
        icon="bold"
        glyphSize={17}
        label="Bold"
        pressed={bold}
        theme={theme}
        onClick={onBold}
        className={cn(styles.slot, bold && styles.on)}
      />
      <IconSlot
        icon="italic"
        glyphSize={17}
        label="Italic"
        pressed={italic}
        theme={theme}
        onClick={onItalic}
        className={cn(styles.slot, italic && styles.on)}
      />

      <span
        className={styles.anchor}
        ref={(el) => {
          anchors.current.align = el;
        }}
      >
        {/* The glyph IS the value — the button shows the alignment in force rather than a generic
            align mark, so the run of three reads as two toggles and one readout. Never lit: it has
            no off state to be lit against, and a permanently filled chip in a run of two that come
            and go would read as a third emphasis that is always on. */}
        <IconSlot
          icon={ALIGN_ICON[align]}
          glyphSize={17}
          label={`Align: ${ALIGN_LABEL[align]}`}
          ariaHasPopup="menu"
          ariaExpanded={open === 'align'}
          theme={theme}
          onClick={() => toggle('align')}
          className={styles.slot}
        />
        {rendered === 'align' ? (
          <div className={cn(styles.menuLayer, styles.menuRight)} data-closing={closing}>
            <MenuSurface width={ALIGN_MENU_WIDTH} theme={theme} label="Alignment">
              {ALIGN_ORDER.map((option) => (
                <MenuRow
                  key={option}
                  icon={ALIGN_ICON[option]}
                  label={ALIGN_LABEL[option]}
                  theme={theme}
                  trailing={
                    option === align ? (
                      <span className={styles.tick}>
                        <DeckIcon name="check" size={16} />
                      </span>
                    ) : undefined
                  }
                  onClick={() => {
                    setOpen(null);
                    if (option !== align) onAlign(option);
                  }}
                />
              ))}
            </MenuSurface>
          </div>
        ) : null}
      </span>

      <span className={styles.spacerNarrow} aria-hidden />
    </div>
  );
}
